import { Spinner } from 'baseui/spinner';
import { useFeatureFlag } from 'context/FeatureFlagContext';
import { useThemeSwitcher } from 'hooks/useThemeSwitcher';
import toast from 'react-hot-toast';
import { Toggle } from 'ui-kit';

import { gql, useMutation, useQuery } from '@apollo/client';

import { DisableChat, EnableChat } from './__generated__/AppPrefs';
const ENABLE_CHAT_MUTATION = gql`
  mutation EnableChat {
    enableChat {
      id
      is_chat_enabled
    }
  }
`;

const DISABLE_CHAT_MUTATION = gql`
  mutation DisableChat {
    disableChat {
      id
      is_chat_enabled
    }
  }
`;

const IS_CHAT_ENABLED_QUERY = gql`
  query IsChatEnabled {
    user {
      id
      company {
        id
        is_chat_enabled
      }
    }
  }
`;

const AppPrefs = () => {
  const { data, loading } = useQuery(IS_CHAT_ENABLED_QUERY);
  const [enableChat] = useMutation<EnableChat>(ENABLE_CHAT_MUTATION);
  const [disableChat] = useMutation<DisableChat>(DISABLE_CHAT_MUTATION);
  const isThemeToggleEnabled = useFeatureFlag('uiThemes');
  const { theme, toggleTheme } = useThemeSwitcher();

  const handleToggle = async (checked: boolean) => {
    if (checked) {
      await enableChat();
      toast.success(
        'Thanks for enabling Chat for your organization. Please be aware that any counterparty must also have enabled Chat before you will be able to use it.',
      );
    } else {
      await disableChat();
    }
  };

  return (
    <div className="space-y-6 p-4">
      {/* Chat Toggle */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase text-gray-500">
            Enable chat for your company
          </span>
          {loading ? (
            <Spinner size={34} />
          ) : (
            <Toggle
              initialState={data?.user?.company?.is_chat_enabled}
              checkedLabel="On"
              uncheckedLabel="Off"
              labelPosition="left"
              onChange={handleToggle}
            />
          )}
        </div>

        {data?.user?.company?.is_chat_enabled && (
          <div className="flex justify-end">
            <div className="max-w-md text-xs text-gray-500 light:text-gray-950">
              Thanks for enabling Chat for your organization. Please be aware that any counterparty
              must also have enabled Chat before you will be able to use it.
            </div>
          </div>
        )}
      </div>

      {isThemeToggleEnabled && (
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase text-gray-500">Theme</span>
          <Toggle
            initialState={theme === 'dark'}
            checkedLabel="Dark"
            uncheckedLabel="Light"
            labelPosition="left"
            onChange={toggleTheme}
          />
        </div>
      )}
    </div>
  );
};

export default AppPrefs;
